import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { 
  Settings,
  Users,
  MessageSquare,
  Target,
  CheckCircle,
  AlertTriangle,
  Loader,
  Shield,
  Activity,
  TrendingUp
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import { useTheme } from '../../../contexts/ThemeContext';
import LineConfiguration from './LineConfiguration';
import UsersAndGroups from './UsersAndGroups';
import RegistrationManagement from './RegistrationManagement';

const LineManagement = () => {
  const { t } = useTranslation();
  const { user, hasRole } = useAuth();
  const { getProtectedTenantInfo } = useTenantProtection();
  const { isDark } = useTheme();
  
  const [activeTab, setActiveTab] = useState('configuration');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalGroups: 0,
    pendingRegistrations: 0,
    activeChannels: 0
  });

  // Check if user has access (Admin or Manager)
  const hasAccess = hasRole(['Admin', 'Manager']);

  // Get current tenant info safely
  const getCurrentTenant = () => {
    return getProtectedTenantInfo();
  };

  // Load Line statistics
  const loadStats = async () => {
    try {
      const tenant = getCurrentTenant();
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/line-management/stats/${tenant.code}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data.data || stats);
      }
    } catch (error) {
      console.error('Failed to load Line statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasAccess) {
      loadStats();
      // Refresh stats every 2 minutes (reduced from 30 seconds to reduce server load)
      const interval = setInterval(loadStats, 2 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [hasAccess]);

  if (!hasAccess) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
          <h3 className={`mt-2 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Access Denied</h3>
          <p className={`mt-1 text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
            You need Admin or Manager permissions to access Line Management.
          </p>
        </div>
      </div>
    );
  }

  const currentTenant = getCurrentTenant();

  const tabs = [
    { id: 'configuration', label: 'Line Configuration', icon: Settings },
    { id: 'users-groups', label: 'Users & Groups', icon: Users },
    { id: 'registrations', label: 'Registration Management', icon: Shield }
  ];

  return (
    <div className={`space-y-6 p-6 ${isDark ? 'bg-gray-900' : 'bg-gray-50'} min-h-screen`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold flex items-center space-x-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <MessageSquare className="h-7 w-7 text-green-600" />
            <span>Line Management</span>
          </h1>
          <p className={`mt-1 flex items-center space-x-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            <span>Tenant: {currentTenant.name} ({currentTenant.code})</span>
          </p>
        </div>
      </div>

      {/* Statistics Cards */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className={`rounded-lg shadow p-6 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Active Channels</p>
                <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.activeChannels}</p>
              </div>
              <Activity className="h-8 w-8 text-green-600" />
            </div>
          </div>

          <div className={`rounded-lg shadow p-6 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Users</p>
                <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.totalUsers}</p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </div>

          <div className={`rounded-lg shadow p-6 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Groups</p>
                <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.totalGroups}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-purple-600" />
            </div>
          </div>

          <div className={`rounded-lg shadow p-6 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pending Registrations</p>
                <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.pendingRegistrations}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-yellow-600" />
            </div>
          </div>
        </div>
      )}

      {/* Tabs Navigation */}
      <div className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  ${activeTab === tab.id
                    ? isDark
                      ? 'border-blue-500 text-blue-400'
                      : 'border-blue-500 text-blue-600'
                    : isDark
                      ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                  flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                `}
              >
                <Icon className="h-5 w-5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            {activeTab === 'configuration' && <LineConfiguration onConfigUpdate={loadStats} />}
            {activeTab === 'users-groups' && <UsersAndGroups />}
            {activeTab === 'registrations' && <RegistrationManagement onUpdate={loadStats} />}
          </>
        )}
      </div>
    </div>
  );
};

export default LineManagement;

